import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY;

function loadHCaptcha() {
  if (document.getElementById('hcaptcha-script')) return;
  const s = document.createElement('script');
  s.id = 'hcaptcha-script';
  s.src = 'https://js.hcaptcha.com/1/api.js';
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

export default function Register() {
  const [form, setForm] = useState({ name: '', phoneNum: '', email: '', password: '', password_confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(!!HCAPTCHA_SITE_KEY);
  const captchaRef = useRef(null);
  const widgetId = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!HCAPTCHA_SITE_KEY) return;
    loadHCaptcha();
    const interval = setInterval(() => {
      if (window.hcaptcha && captchaRef.current && widgetId.current === null) {
        widgetId.current = window.hcaptcha.render(captchaRef.current, {
          sitekey: HCAPTCHA_SITE_KEY,
          theme: 'dark',
          hl: 'es', // idioma del desafío en español
          callback: token => setCaptchaToken(token),
          'expired-callback': () => setCaptchaToken(''),
        });
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) return setError('La contraseña debe tener al menos 6 caracteres');
    if (form.password !== form.password_confirm) return setError('Las contraseñas no coinciden');
    if (captchaRequired && !captchaToken) return setError('Completa el captcha para continuar');

    setLoading(true);
    try {
      const { password_confirm, phoneNum, ...rest } = form;
      const phone = '+593' + phoneNum.replace(/\D/g, '');
      const payload = { ...rest, phone };
      const res = await fetch('/api/auth/register-client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, captchaToken }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 429) setCaptchaRequired(true);
        setError(data.error || 'Error al registrar');
        if (window.hcaptcha && widgetId.current !== null) {
          window.hcaptcha.reset(widgetId.current);
          setCaptchaToken('');
        }
        return;
      }

      if (data.needsPhoneVerification) {
        navigate('/verify-phone', { state: { userId: data.userId, phone: data.phone } });
      } else {
        navigate('/client');
      }
    } catch {
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-layout">
      <form onSubmit={handleSubmit} className="auth-card">
        <div className="auth-brand">
          <img src="/logo.png" alt="Autonation" className="auth-logo auth-logo-xl" onError={e => { e.target.style.display = 'none'; }} />
          <div className="auth-brand-sub">Nueva cuenta cliente</div>
        </div>

        <div className="form-group">
          <label className="form-label">Nombre completo</label>
          <input className="form-input" placeholder="Carlos Mendoza"
            value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus />
        </div>

        <div className="form-group">
          <label className="form-label">Teléfono (WhatsApp)</label>
          <div style={{ display: 'flex', gap: 0 }}>
            <span
              style={{
                display: 'flex', alignItems: 'center', padding: '0 0.75rem',
                background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)',
                borderRight: 'none', borderRadius: '10px 0 0 10px',
                color: 'var(--silver-light)', fontWeight: 600, fontSize: '0.92rem',
                whiteSpace: 'nowrap', userSelect: 'none',
              }}
            >
              🇪🇨 +593
            </span>
            <input
              className="form-input"
              placeholder="99 024 6733"
              style={{ borderRadius: '0 10px 10px 0', flex: 1 }}
              value={form.phoneNum}
              onChange={e => setForm(f => ({ ...f, phoneNum: e.target.value }))}
              required
              inputMode="tel"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Email (opcional)</label>
          <input className="form-input" type="email" placeholder="tu@email.com"
            value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
        </div>

        <div className="form-group">
          <label className="form-label">Contraseña</label>
          <input className="form-input" type="password" placeholder="Mín. 6 caracteres"
            value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={6} />
        </div>

        <div className="form-group">
          <label className="form-label">Confirmar contraseña</label>
          <input className="form-input" type="password" placeholder="Reingresa la contraseña"
            value={form.password_confirm} onChange={e => setForm(f => ({ ...f, password_confirm: e.target.value }))} required minLength={6} />
        </div>

        {captchaRequired && HCAPTCHA_SITE_KEY && (
          <div style={{ margin: '0.5rem 0' }}>
            <div ref={captchaRef} />
          </div>
        )}

        {error && <div className="error-text">{error}</div>}

        <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: '1rem' }}>
          {loading ? 'Creando cuenta…' : 'Registrarse →'}
        </button>

        <div className="hint-text" style={{ marginTop: '1.2rem' }}>
          ¿Ya tienes cuenta? <Link to="/login" className="link">Ingresar</Link>
        </div>
      </form>
    </div>
  );
}
